import React, { useState } from 'react';
import { Settings, Bell, UploadCloud, Wrench, Bug, Globe, Clock, Trash2, RefreshCw, Palette, Moon, Sun, Monitor, ImageIcon, Shield } from 'lucide-react';

const AdminSettings = () => {
  const [settings, setSettings] = useState([
    {
      id: 'popular-services',
      title: 'Customer Service Manage Application',
      subtitle: 'Manage customer-facing service tiles',
      icon: 'pricetags',
      type: 'button',
      value: false,
    },
    {
      id: 'membership-plans',
      title: 'RSA Membership Plans',
      subtitle: 'Subscription plans for customer app',
      icon: 'shield',
      type: 'button',
      value: false,
    },
    {
      id: 'notifications',
      title: 'Push Notifications',
      subtitle: 'Enable system notifications',
      icon: 'bell',
      type: 'toggle',
      value: true,
    },
    {
      id: 'auto-backup',
      title: 'Auto Backup',
      subtitle: 'Automatic data backup',
      icon: 'upload-cloud',
      type: 'toggle',
      value: false,
    },
    {
      id: 'maintenance-mode',
      title: 'Maintenance Mode',
      subtitle: 'Enable system maintenance',
      icon: 'wrench',
      type: 'toggle',
      value: false,
    },
    {
      id: 'debug-mode',
      title: 'Debug Mode',
      subtitle: 'Enable debug logging',
      icon: 'bug',
      type: 'toggle',
      value: true,
    },
    {
      id: 'language',
      title: 'Language',
      subtitle: 'System language',
      icon: 'globe',
      type: 'select',
      options: ['English', 'Spanish', 'French', 'German'],
    },
    {
      id: 'timezone',
      title: 'Timezone',
      subtitle: 'System timezone',
      icon: 'clock',
      type: 'select',
      options: ['UTC', 'EST', 'PST', 'GMT'],
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      subtitle: 'Configure data retention policy',
      icon: 'trash',
      type: 'button',
    },
    {
      id: 'system-update',
      title: 'System Update',
      subtitle: 'Check for updates',
      icon: 'refresh-cw',
      type: 'button',
    },
  ]);

  const [themeMode, setThemeMode] = useState('system');

  const handleSettingChange = (id, value) => {
    setSettings(prev =>
      prev.map(setting =>
        setting.id === id
          ? { ...setting, value: value !== undefined ? value : !setting.value }
          : setting
      )
    );
  };

  const handleSettingPress = (setting) => {
    if (setting.type === 'button') {
      if (setting.id === 'popular-services') {
        window.location.href = '/popular-services';
        return;
      }
      if (setting.id === 'customer-banners') {
        window.location.href = '/customer-app-banners';
        return;
      }
      if (setting.id === 'membership-plans') {
        window.location.href = '/membership-plans';
        return;
      }
      alert(`${setting.title} configuration`);
    } else if (setting.type === 'select') {
      alert(`Select ${setting.title} option`);
    }
  };

  const getIconComponent = (iconName) => {
    const icons = {
      pricetags: Settings,
      image: ImageIcon,
      shield: Shield,
      bell: Bell,
      'upload-cloud': UploadCloud,
      wrench: Wrench,
      bug: Bug,
      globe: Globe,
      clock: Clock,
      trash: Trash2,
      'refresh-cw': RefreshCw,
    };
    return icons[iconName] || Settings;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">System Settings</h1>
        <p className="text-gray-600 mt-1">Configure system preferences</p>
      </div>

      {/* Theme Mode Selector */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Palette className="text-primary-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Appearance</h3>
              <p className="text-sm text-gray-600">Theme: {themeMode}</p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setThemeMode('light')}
              className={`px-4 py-2 rounded-lg border transition ${
                themeMode === 'light'
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Sun size={18} className="inline mr-2" />
              Light
            </button>
            <button
              onClick={() => setThemeMode('dark')}
              className={`px-4 py-2 rounded-lg border transition ${
                themeMode === 'dark'
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Moon size={18} className="inline mr-2" />
              Dark
            </button>
            <button
              onClick={() => setThemeMode('system')}
              className={`px-4 py-2 rounded-lg border transition ${
                themeMode === 'system'
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Monitor size={18} className="inline mr-2" />
              System
            </button>
          </div>
        </div>
      </div>

      {/* Settings List */}
      <div className="space-y-3">
        {settings.map((setting) => {
          const IconComponent = getIconComponent(setting.icon);
          return (
            <div
              key={setting.id}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 flex items-center justify-between"
            >
              <div className="flex items-center space-x-4 flex-1">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <IconComponent size={20} className="text-gray-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-900">{setting.title}</h3>
                  <p className="text-sm text-gray-600">{setting.subtitle}</p>
                </div>
              </div>

              <div className="flex items-center">
                {setting.type === 'toggle' && (
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={setting.value}
                      onChange={(e) => handleSettingChange(setting.id, e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                  </label>
                )}
                {setting.type === 'button' && (
                  <button
                    onClick={() => handleSettingPress(setting)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
                {setting.type === 'select' && (
                  <button
                    onClick={() => handleSettingPress(setting)}
                    className="px-3 py-1 bg-gray-100 rounded-lg text-sm text-gray-700 hover:bg-gray-200 flex items-center space-x-2"
                  >
                    <span>Select</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminSettings;
